
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { AllocationsHeader } from "./prize-allocations/AllocationsHeader";
import { AllocationDialogs } from "./prize-allocations/AllocationDialogs";
import { AllocationsTable } from "./prize-allocations/AllocationsTable";
import { UniversityResetControls } from "./prize-allocations/UniversityResetControls";
import { AllocationsSkeleton } from "./prize-allocations/AllocationsSkeleton";
import { usePrizeAllocationForm } from "@/hooks/usePrizeAllocationForm";
import { usePrizeAllocationsData } from "./prize-allocations/usePrizeAllocationsData";
import { getActiveUniversity } from "@/utils/prize/universityUtils";
import type { PrizeAllocation } from "./prize-allocations/types";

export function PrizeAllocationsManagement() {
  const { toast } = useToast();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<PrizeAllocation | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<string>("all");

  const { allocations, prizes, universities, isLoading, refetch } = usePrizeAllocationsData();

  useEffect(() => {
    const initializeActiveUniversity = async () => {
      const activeUniversity = await getActiveUniversity();
      setSelectedUniversity(activeUniversity.id.toString());
    };
    
    initializeActiveUniversity();
  }, []);

  // Filter allocations based on selected university
  const filteredAllocations = selectedUniversity === "all" 
    ? allocations 
    : allocations.filter(allocation => allocation.university_id.toString() === selectedUniversity);

  const { formData, setFormData, handleSubmit } = usePrizeAllocationForm(() => {
    setIsAddOpen(false);
    setIsEditOpen(false);
    refetch();
  });

  const handleResetAllocations = async (universityId: number) => {
    try {
      setIsResetting(true);
      console.log('Resetting allocations for university:', universityId);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      const { error } = await supabase.rpc('reset_prize_allocations', {
        university_id_param: universityId,
        reset_by_param: user.id
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Allocations have been reset successfully",
      });

      refetch();
    } catch (error: any) {
      console.error('Error resetting allocations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to reset allocations",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      console.log('Deleting allocation:', id);
      const { error } = await supabase
        .from('prize_allocations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({ 
        title: "Success", 
        description: "Allocation deleted successfully" 
      });
      
      refetch();
    } catch (error: any) {
      console.error('Delete operation failed:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete allocation",
      });
    }
  };

  const handleEdit = (allocation: PrizeAllocation) => {
    setEditingAllocation(allocation);
    setFormData({
      university_id: allocation.university_id.toString(),
      prize_id: allocation.prize_id.toString(),
      daily_limit: allocation.daily_limit,
    });
    setIsEditOpen(true);
  };

  console.log('Current allocations:', allocations);
  console.log('Loading state:', isLoading);

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <AllocationsHeader onAddClick={() => setIsAddOpen(true)} />
          <Select value={selectedUniversity} onValueChange={setSelectedUniversity}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by University" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Universities</SelectItem>
              {universities.map((university) => (
                <SelectItem key={university.id} value={university.id.toString()}>
                  {university.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <AllocationDialogs
        isAddOpen={isAddOpen}
        isEditOpen={isEditOpen}
        setIsAddOpen={setIsAddOpen}
        setIsEditOpen={setIsEditOpen}
        prizes={prizes}
        universities={universities}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        editingAllocation={editingAllocation}
      />

      {isLoading ? (
        <AllocationsSkeleton />
      ) : (
        <AllocationsTable
          allocations={filteredAllocations}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <UniversityResetControls
        universities={universities}
        allocations={allocations}
        isResetting={isResetting}
        onReset={handleResetAllocations}
      />
    </Card>
  );
}
